import { useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppSubShell, Card, CardTitle } from '@up2d8/ui';

/**
 * "Wat ziet je werkgever?"
 *
 * Deze pagina is een belofte uit ADR-0014 en uit het bouwplan: de medewerker ziet in de app wat de
 * werkgever kan zien. Hij staat in de app en niet in een privacyverklaring op een website, omdat
 * dit de plek is waar de vraag opkomt — onder de knop "Gelezen en begrepen".
 *
 * De tekst is met opzet in de tweede persoon en zonder juridische termen. Wie dit leest, staat
 * meestal in werkkleding.
 */
export function AppPrivacyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const seen: string[] = t('privacy.seenList', { returnObjects: true }) as unknown as string[];
  const notSeen: string[] = t('privacy.notSeenList', {
    returnObjects: true,
  }) as unknown as string[];

  return (
    <AppSubShell title={t('privacy.title')} onBack={() => void navigate({ to: '/app/profile' })}>
      <Card>
        <p className="text-content text-text-secondary">{t('privacy.intro')}</p>
      </Card>

      <Card>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            <Eye aria-hidden className="h-5 w-5 text-brand-primary" />
            {t('privacy.seenTitle')}
          </span>
        </CardTitle>
        <ul className="mt-element flex flex-col gap-2 text-content text-text-primary">
          {seen.map((line) => (
            <li key={line} className="flex gap-2">
              <span aria-hidden>•</span>
              {line}
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            <EyeOff aria-hidden className="h-5 w-5 text-text-muted" />
            {t('privacy.notSeenTitle')}
          </span>
        </CardTitle>
        <ul className="mt-element flex flex-col gap-2 text-content text-text-primary">
          {notSeen.map((line) => (
            <li key={line} className="flex gap-2">
              <span aria-hidden>•</span>
              {line}
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            <ShieldCheck aria-hidden className="h-5 w-5 text-brand-primary" />
            {t('privacy.namesTitle')}
          </span>
        </CardTitle>
        <p className="mt-element text-content text-text-secondary">{t('privacy.namesBody')}</p>
      </Card>

      {/*
        Wat je leidinggevende ziet (Stap 17, TEAM-1, spec §5).

        Een eigen kaart en geen regel bij "wie ziet je naam": dat laatste gaat over een recht dat
        het bedrijf toekent, dit over een persoon die de medewerker elke dag spreekt. Wie die twee
        samenvoegt, laat de belangrijkste vraag - wat ziet mijn chef - verdwijnen in een alinea
        over rechten.
      */}
      <Card>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            <ShieldCheck aria-hidden className="h-5 w-5 text-brand-primary" />
            {t('privacy.managerTitle')}
          </span>
        </CardTitle>
        <p className="mt-element text-content text-text-secondary" data-testid="privacy-manager">
          {t('privacy.managerBody')}
        </p>
      </Card>

      <p className="text-small text-text-muted">{t('privacy.questions')}</p>
    </AppSubShell>
  );
}
